"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { PhoneCall, FileText, Flame, Wrench, TriangleAlert as AlertTriangle, ChevronDown, MapPin, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function BrandService() {
  const shouldReduceMotion = useReducedMotion();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(prev => prev === index ? null : index);
  };
  const waterHeaterIssues = [{
    title: "No Hot Water",
    description: "Burner failures, faulty heating elements, or tripped thermostats across gas & electric brands.",
    primaryLink: "/water-heater-problems/",
    secondaryLink: "/water-heater-replacement/",
    primaryText: "Diagnose Hot Water",
    secondaryText: "Replacement Options"
  }, {
    title: "Tank Leaking & Pooling",
    description: "Corroded tank bottoms, failed relief valves, or fitting cracks requiring immediate mitigation.",
    primaryLink: "/water-heater-problems/",
    secondaryLink: "/water-heater-replacement/",
    primaryText: "Leak Diagnostic",
    secondaryText: "Tank Replacement"
  }, {
    title: "Tankless Error Codes",
    description: "Ignition lockouts, flow sensor errors, and exhaust blockage codes on Rinnai, Navien & Noritz.",
    primaryLink: "/water-heater-problems/",
    secondaryLink: "/water-heater-replacement/",
    primaryText: "Check Error Codes",
    secondaryText: "Upgrade Unit"
  }, {
    title: "Scale & Sediment Buildup",
    description: "Mineral deposits causing rumbling tank noises, reduced efficiency, and heating element burnout.",
    primaryLink: "/water-heater-problems/",
    secondaryLink: "/water-heater-replacement/",
    primaryText: "Sediment Solutions",
    secondaryText: "System Replacement"
  }];
  const fixtureIssues = [{
    title: "Dripping & Leaking Faucets",
    description: "Worn ceramic cartridges, O-rings, and valve seats on Moen, Delta, Kohler, and American Standard.",
    primaryLink: "/fixture-problems/",
    secondaryLink: "/toilet-problems/",
    primaryText: "Fixture Diagnostics",
    secondaryText: "Toilet & Fixture Care"
  }, {
    title: "Under-Sink Valve Leaks",
    description: "Corroded shutoff angle stops, braided supply line failures, and loose compression ferrules.",
    primaryLink: "/fixture-problems/",
    secondaryLink: "/toilet-problems/",
    primaryText: "Valve Repair Help",
    secondaryText: "Associated Repairs"
  }, {
    title: "Constantly Running Toilets",
    description: "Faulty fill valves, cracked overflow tubes, and warped flappers wasting hundreds of gallons.",
    primaryLink: "/toilet-problems/",
    secondaryLink: "/fixture-problems/",
    primaryText: "Toilet Troubleshooting",
    secondaryText: "Fixture Inspections"
  }, {
    title: "Flapper & Seal Failures",
    description: "Deteriorated rubber seals causing silent phantom flushing and high Clayton County water bills.",
    primaryLink: "/toilet-problems/",
    secondaryLink: "/fixture-problems/",
    primaryText: "Flapper Diagnostic",
    secondaryText: "Fixture Services"
  }];
  const faqItems = [{
    question: "What plumbing brands do you service?",
    answer: <span>
          We service and repair all premier residential plumbing brands including Rheem, Bradford White, A.O. Smith, Navien, Rinnai, Moen, Delta, Kohler, American Standard, InSinkErator, and Gerber. Our technicians carry genuine OEM and compatible factory-spec parts for urgent repairs. Explore our complete range of solutions on our{" "}
          <Link to="/services/" className="font-semibold text-primary underline underline-offset-4 hover:text-accent">
            Plumbing Services
          </Link>{" "}
          page.
        </span>
  }, {
    question: "Do you service tankless water heater brands?",
    answer: <span>
          Yes, our licensed plumbing specialists diagnose, descale, flush, and repair top tankless brands such as Rinnai, Navien, Noritz, Rheem, and Bosch. Whether you are dealing with ignition failure codes or low hot water pressure, we offer targeted repairs via our{" "}
          <Link to="/water-heater-repair/" className="font-semibold text-primary underline underline-offset-4 hover:text-accent">
            Water Heater Repair
          </Link>{" "}
          service.
        </span>
  }, {
    question: "Do you repair Rheem water heaters?",
    answer: <span>
          Absolutely. Rheem is one of the most common water heater brands in Clayton County homes. We service gas, electric, hybrid heat pump, and tankless Rheem models, providing fast element replacements, gas control valve diagnostics, and anode rod servicing through our{" "}
          <Link to="/water-heater-repair/" className="font-semibold text-primary underline underline-offset-4 hover:text-accent">
            Water Heater Repair
          </Link>{" "}
          department.
        </span>
  }, {
    question: "Do you service Bradford White water heaters?",
    answer: <span>
          Yes. Bradford White water heaters are built for durability and are contractor-grade systems. We stock certified replacement components including pilot assemblies, immersion thermostats, and temperature-and-pressure relief valves to get your hot water back online immediately.
        </span>
  }, {
    question: "Do you service Moen faucets?",
    answer: <span>
          Yes, our plumbers regularly repair and rebuild Moen kitchen and bathroom fixtures, including cartridge replacements (1225, 1200, and 1222 Positemp valves) to stop leaks and restore optimal flow without needing a full fixture tear-out unless requested.
        </span>
  }, {
    question: "Do you service Delta fixtures?",
    answer: <span>
          Yes. We provide complete service for Delta faucets, shower valves, and tub spouts, including ball assemblies, cam & packing kits, and DST cartridge replacements. When clogs also accompany fixture backups, our{" "}
          <Link to="/drain-cleaning/" className="font-semibold text-primary underline underline-offset-4 hover:text-accent">
            Drain Cleaning
          </Link>{" "}
          service provides full system clearance.
        </span>
  }, {
    question: "How do I request brand-specific plumbing service?",
    answer: <span>
          You can request service immediately by calling our 24/7 direct-dial dispatch at (470) 847-9076 or submitting your unit details through our online booking portal. Inform our technician of the brand and model number so we arrive fully equipped with the appropriate manufacturer components.
        </span>
  }];
  const serviceLocations = ["Downtown Jonesboro", "Irondale", "Riverdale", "Morrow", "Forest Park", "Lake City", "Lovejoy", "Bonanza", "Rex", "Tara Boulevard Corridor", "Panhandle Road Area", "Fayetteville Border Communities"];
  return <div id="need-brand-specific-service" className="space-y-16 md:space-y-24 bg-background py-14 md:py-20 lg:py-24">
      {/* ZONE 1: Common Brand Problems Hub */}
      <section className="mx-auto max-w-[1240px] px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="mb-3 inline-flex items-center gap-2 rounded-lg bg-secondary px-3.5 py-1.5 text-xs font-bold tracking-wider text-secondary-foreground uppercase">
            <Wrench className="size-3.5 text-accent shrink-0" />
            <span>Brand Troubleshooting & Diagnostic Hub</span>
          </div>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Common Brand Problems We Resolve Daily
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Manufacturer-specific plumbing failures require precise diagnostic protocols. Select your problem cluster below for immediate troubleshooting and replacement pathways.
          </p>
        </div>

        {/* Two problem clusters */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Cluster 1: Water Heater Issues */}
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-md">
            <div className="flex items-center gap-3 border-b border-border pb-5 mb-6">
              <div className="flex size-12 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                <Flame className="size-6 text-accent" />
              </div>
              <div>
                <h3 className="font-default text-xl font-bold text-card-foreground sm:text-2xl">
                  Water Heater Issues
                </h3>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Rheem, Bradford White, A.O. Smith & Tankless Brands
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {waterHeaterIssues.map((issue, i) => <div key={issue.title} data-index={i} className="rounded-lg border border-border/80 bg-background p-4 transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-default text-base font-bold text-foreground">
                      {issue.title}
                    </h4>
                    <span className="inline-flex size-2 mt-1.5 rounded-lg bg-accent shrink-0" />
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {issue.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 pt-2 border-t border-border/50">
                    <Link to={issue.primaryLink} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-accent transition-colors">
                      <span>{issue.primaryText}</span>
                      <ArrowRight className="size-3" />
                    </Link>
                    <span className="text-xs text-muted-foreground/60">•</span>
                    <Link to={issue.secondaryLink} className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors">
                      <span>{issue.secondaryText}</span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Cluster 2: Fixture & Valve Issues */}
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-md">
            <div className="flex items-center gap-3 border-b border-border pb-5 mb-6">
              <div className="flex size-12 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                <AlertTriangle className="size-6 text-accent" />
              </div>
              <div>
                <h3 className="font-default text-xl font-bold text-card-foreground sm:text-2xl">
                  Fixture & Valve Issues
                </h3>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Moen, Delta, Kohler & American Standard Units
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {fixtureIssues.map((issue, i) => <div key={issue.title} data-index={i} className="rounded-lg border border-border/80 bg-background p-4 transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-default text-base font-bold text-foreground">
                      {issue.title}
                    </h4>
                    <span className="inline-flex size-2 mt-1.5 rounded-lg bg-accent shrink-0" />
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {issue.description}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 pt-2 border-t border-border/50">
                    <Link to={issue.primaryLink} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-accent transition-colors">
                      <span>{issue.primaryText}</span>
                      <ArrowRight className="size-3" />
                    </Link>
                    <span className="text-xs text-muted-foreground/60">•</span>
                    <Link to={issue.secondaryLink} className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors">
                      <span>{issue.secondaryText}</span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ZONE 2: Service Areas Section */}
      <section className="bg-secondary/40 py-12 md:py-16 border-y border-border">
        <div className="mx-auto max-w-[1240px] px-5 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <div className="mb-3 inline-flex items-center gap-2 rounded-lg bg-background px-3.5 py-1.5 text-xs font-bold tracking-wider text-primary border border-border uppercase">
                <MapPin className="size-3.5 text-accent shrink-0" />
                <span>Local Clayton County Dispatch</span>
              </div>
              <h2 className="font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
                Brand Plumbing Service Across Jonesboro, GA & Clayton County
              </h2>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                Our service trucks are fully stocked with manufacturer-certified components and staged across Jonesboro to guarantee quick arrival times for emergency water heater, faucet, and pipe breakdowns.
              </p>
              <div className="mt-6">
                <Button asChild variant="default" className="rounded-lg bg-primary px-6 py-5 text-sm font-bold text-primary-foreground shadow-sm transition-[color,background-color,border-color,box-shadow] hover:bg-primary/90">
                  <Link to="/service-areas/" className="inline-flex items-center gap-2">
                    <span>View Service Areas</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-border bg-background p-6 md:p-8 shadow-md">
                <h3 className="font-default text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <ShieldCheck className="size-5 text-accent shrink-0" />
                  <span>Immediate Brand Support Locations</span>
                </h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {serviceLocations.map((loc, i) => <div key={loc} data-index={i} className="flex items-center gap-2 text-xs md:text-sm font-medium text-foreground bg-secondary/50 rounded-lg p-2.5 border border-border/50">
                      <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                      <span className="truncate">{loc}</span>
                    </div>)}
                </div>
                <p className="mt-4 text-xs text-muted-foreground">
                  Need dispatch outside listed areas? Contact our team for rapid routing across neighboring South Metro Atlanta corridors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ZONE 3: FAQ Accordion */}
      <section className="mx-auto max-w-[960px] px-5 md:px-8">
        <div className="text-center mb-10">
          <div className="mb-3 inline-flex items-center gap-2 rounded-lg bg-secondary px-3.5 py-1.5 text-xs font-bold tracking-wider text-secondary-foreground uppercase">
            <HelpCircle className="size-3.5 text-accent shrink-0" />
            <span>Frequently Asked Questions</span>
          </div>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Brand Plumbing & Water Heater FAQs
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            Get clear answers regarding manufacturer warranties, compatible components, and brand service availability in Jonesboro.
          </p>
        </div>

        <div className="space-y-3">
          {faqItems.map((item, index) => {
          const isOpen = openFaqIndex === index;
          return <div key={item.question} data-index={index} className="overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-[border-color,box-shadow] hover:border-primary/30">
                <Button type="button" variant="ghost" onClick={() => toggleFaq(index)} className="h-auto w-full justify-between gap-4 p-5 text-left font-default text-base md:text-lg font-bold text-card-foreground whitespace-normal rounded-none hover:bg-transparent hover:text-primary focus-visible:ring-0" aria-expanded={isOpen}>
                  <span className="text-left">{item.question}</span>
                  <ChevronDown className={`size-5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180 text-primary" : ""}`} />
                </Button>

                <AnimatePresence initial={false}>
                  {isOpen && <motion.div initial={shouldReduceMotion ? {
                opacity: 1,
                height: "auto"
              } : {
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: "auto"
              }} exit={shouldReduceMotion ? {
                opacity: 0,
                height: "auto"
              } : {
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.25,
                ease: "easeOut"
              }} className="overflow-hidden">
                      <div className="border-t border-border/60 px-5 pt-3 pb-5 text-sm md:text-base leading-relaxed text-muted-foreground">
                        {item.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </section>

      {/* ZONE 4: Closing Navy CTA Banner */}
      <section className="mx-auto max-w-[1240px] px-5 md:px-8">
        <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-primary px-6 py-10 md:p-12 lg:p-14 text-primary-foreground shadow-xl">
          {/* Decorative motif element */}
          <div className="absolute top-0 bottom-0 left-0 w-2 bg-accent" />
          <div className="absolute -right-20 -bottom-20 size-72 rounded-lg bg-accent/10 blur-3xl pointer-events-none" />

          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12 relative z-10">
            <div className="lg:col-span-7">
              <div className="mb-3 inline-flex items-center gap-2 rounded-lg bg-primary-foreground/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent backdrop-blur-xs">
                <span>Immediate Brand Dispatch</span>
              </div>
              <h2 className="font-default text-2xl font-bold tracking-tight text-primary-foreground sm:text-3xl lg:text-4xl">
                Need Service for a Specific Brand in Jonesboro?
              </h2>
              <p className="mt-3 text-base text-primary-foreground/80 sm:text-lg leading-relaxed">
                Connect directly with our 24/7 on-call master plumbers. We diagnose your exact make and model with OEM-grade precision and upfront pricing.
              </p>
            </div>

            <div className="flex flex-col gap-3.5 sm:flex-row lg:col-span-5 lg:flex-col justify-center">
              <Button asChild size="lg" className="w-full rounded-lg bg-accent px-6 py-6 text-base font-bold text-accent-foreground shadow-md transition-[color,background-color,border-color,box-shadow] hover:bg-accent/90">
                <a href="tel:(470) 847-9076" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-5 shrink-0" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full rounded-lg border-primary-foreground/30 bg-primary-foreground/10 px-6 py-6 text-base font-semibold text-primary-foreground shadow-sm backdrop-blur-xs transition-[color,background-color,border-color,box-shadow] hover:bg-primary-foreground hover:text-primary">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <FileText className="size-5 shrink-0" />
                  <span>Book Online Service</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}